/** @jsx jsx */
import React, { useCallback, useState, useMemo, type PropsWithChildren, Fragment } from 'react';

import { jsx } from '@compiled/react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { useFragment, graphql, useMutation } from 'react-relay';
import { Box, xcss } from '@atlaskit/primitives';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import CascadingEditViewEntryPoint from '@atlassian/jira-issue-field-cascading-select-editview-full/src/entrypoint.tsx';
import { FieldInlineEditLiteWithEntryPoint } from '@atlassian/jira-issue-field-inline-edit-lite/src/ui/index.tsx';
import type {
	CascadingSelectEditViewProps,
	CascadingSelectValuePair,
	NullableCascadingSelectValuePair,
} from '@atlassian/jira-issue-field-cascading-select-editview-full/src/ui/cascading-select/types.tsx';
import { CascadingSelectReadView } from '@atlassian/jira-issue-field-cascading-select-readview-full/src/ui/cascading-select/index.tsx';
import { useInlineEditFieldInjections } from '@atlassian/jira-issue-field-injections/src/controllers/inline-edit-injections-context/index.tsx';
import { useFieldInlineEditActions } from '@atlassian/jira-issue-field-inline-edit-actions/src/controllers/index.tsx';

import { useBespokeEmptyFieldTextExperiment } from '@atlassian/jira-issue-experiments/src/bespoke-empty-field-text-experiment/index.tsx';
import type { OnSubmitCallbacks } from '@atlassian/jira-issue-field-inline-edit-actions/src/controllers/types.tsx';
import type { ValidationFieldProps } from '@atlassian/jira-issue-field-inline-edit-lite/src/ui/field-inline-edit-lite/types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { createMutationErrorHandlers } from '@atlassian/jira-issue-field-inline-edit-actions/src/controllers/utils/extract-error-message.tsx';
import type { cascadingSelect_issueFieldCascadingSelectInlineEditFull_CascadingSelectInlineEditViewIsEditable_fragmentRef$key as CascadingSelectIsEditableFragment } from './__generated__/cascadingSelect_issueFieldCascadingSelectInlineEditFull_CascadingSelectInlineEditViewIsEditable_fragmentRef.graphql';
import type { cascadingSelect_issueFieldCascadingSelectInlineEditFull_CascadingSelectInlineEditView_fragmentRef$key as CascadingSelectFragment } from './__generated__/cascadingSelect_issueFieldCascadingSelectInlineEditFull_CascadingSelectInlineEditView_fragmentRef.graphql';
import type {
	cascadingSelect_issueFieldCascadingSelect_CascadingSelectFieldIsEditable_Mutation as CascadingSelectIsEditableMutation,
	cascadingSelect_issueFieldCascadingSelect_CascadingSelectFieldIsEditable_Mutation$rawResponse as CascadingSelectIsEditableMutationResponse,
} from './__generated__/cascadingSelect_issueFieldCascadingSelect_CascadingSelectFieldIsEditable_Mutation.graphql';

import type {
	CascadingSelectInlineEditViewIsEditableProps,
	CascadingSelectInlineEditViewProps,
} from './types.tsx';

const MaybeUFOSegment = componentWithFG(
	'nin_blindspot_fixes_fy26',
	({ children }: PropsWithChildren<{}>) => (
		<UFOSegment name="issue-field-cascading-select-read-view" mode="list">
			{children}
		</UFOSegment>
	),
	Fragment,
);

/** Check if the new selected value is equal to the old value */
const isEqualSelection = (
	a: NullableCascadingSelectValuePair,
	b: NullableCascadingSelectValuePair,
) =>
	a?.parentOptionValue?.value === b?.parentOptionValue?.value &&
	a?.childOptionValue?.value === b?.childOptionValue?.value;

const EditViewWrapper = ({ children }: PropsWithChildren<{}>) => {
	// This needs to be fixed by DST: go/DSP-22083
	// Dropdown z-index is 1, action bar is 200
	// Bento fixes this by wrapping dropdown into a new 300 stack context
	return <Box xcss={wrapperStyles}>{children}</Box>;
};

/**
 * Inline edit will handle the switching behaviour between the 'readView' and 'editView' components.
 *
 * @param props [CascadingSelectInlineEditViewProps](./types.tsx)
 */
export const CascadingSelectInlineEditViewIsEditable = ({
	attributes,
	editViewPopup,
	editViewPopupAlignBlock,
	editViewPopupWidth,
	editViewPopupTarget,
	readViewFitContainerHeight,
	hideActionButtons = false,
	menuPosition,
	fragmentRef,
	onSubmit,
	onSubmitFailed,
	onSubmitSucceeded,
	spacing = 'default',
	isTruncated = false,
	isEditable = false,
	shouldRenderHoverPopover,
}: CascadingSelectInlineEditViewIsEditableProps): JSX.Element => {
	const { isExperimentEnabled } = useBespokeEmptyFieldTextExperiment();

	// #region Relay
	const data = useFragment<CascadingSelectIsEditableFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment cascadingSelect_issueFieldCascadingSelectInlineEditFull_CascadingSelectInlineEditViewIsEditable_fragmentRef on JiraCascadingSelectField {
				id
				name
				fieldId
				type
				...cascadingSelect_issueFieldCascadingSelectReadviewFull_CascadingSelectReadView
				cascadingOption {
					parentOptionValue {
						id
						value
						isDisabled
					}
					childOptionValue {
						id
						value
						isDisabled
					}
				}
			}
		`,
		fragmentRef,
	);

	// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
	const [commit] = useMutation<CascadingSelectIsEditableMutation>(graphql`
		mutation cascadingSelect_issueFieldCascadingSelect_CascadingSelectFieldIsEditable_Mutation(
			$input: JiraUpdateCascadingSelectFieldInput!
		) @raw_response_type {
			jira @optIn(to: ["JiraIssueFieldMutations"]) {
				updateCascadingSelectField(input: $input) {
					success
					errors {
						message
					}
					field {
						...cascadingSelect_issueFieldCascadingSelectInlineEditFull_CascadingSelectInlineEditViewIsEditable_fragmentRef
					}
				}
			}
		}
	`);

	const { id: uniqueFieldId, cascadingOption, name, fieldId, type: fieldType } = data;
	// #endregion

	// #region State
	const initialValue: CascadingSelectValuePair = useMemo(
		() => ({
			parentOptionValue: cascadingOption?.parentOptionValue
				? {
						id: cascadingOption?.parentOptionValue?.id ?? '',
						value: cascadingOption?.parentOptionValue?.value ?? '',
						isDisabled: cascadingOption?.parentOptionValue?.isDisabled,
					}
				: null,
			childOptionValue: cascadingOption?.childOptionValue
				? {
						id: cascadingOption?.childOptionValue?.id ?? '',
						value: cascadingOption?.childOptionValue?.value ?? '',
						isDisabled: cascadingOption?.childOptionValue?.isDisabled,
					}
				: null,
		}),
		[cascadingOption?.childOptionValue, cascadingOption?.parentOptionValue],
	);
	const [updatedValue, setUpdatedValue] = useState<NullableCascadingSelectValuePair>(initialValue);

	const { overriding } = useInlineEditFieldInjections();
	const { overrideLabel, overrideIsEditable } = overriding;

	const fieldName = useMemo(() => overrideLabel(name), [overrideLabel, name]);

	const isFieldEditable = useMemo(
		() => overrideIsEditable(isEditable),
		[overrideIsEditable, isEditable],
	);
	// #endregion

	// #region Action
	const handleSubmit = useCallback(
		(newOption: CascadingSelectValuePair | null, { onSuccess, onFail }: OnSubmitCallbacks) => {
			onSubmit?.(newOption);
			const onCompleted = fg('thor_inline-edit-actions-error-extraction')
				? createMutationErrorHandlers({ onSuccess, onFail })
				: // eslint-disable-next-line @typescript-eslint/no-explicit-any
					(response: any) => {
						if (response.jira?.updateCascadingSelectField?.success) {
							onSuccess();
						} else {
							onFail();
						}
					};
			// note: If these objects happen to include more than what is expected, Relay will complain.
			const { parentOptionValue, childOptionValue } = newOption || {};

			commit({
				variables: {
					input: {
						id: uniqueFieldId,
						operation: {
							operation: 'SET',
							parentOption: parentOptionValue?.id || null,
							childOption: childOptionValue?.id || null,
						},
					},
				},
				onCompleted,
				onError: (error) => onFail(error, error.message),
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				optimisticResponse: {
					jira: {
						updateCascadingSelectField: {
							success: true,
							errors: null,
							field: {
								id: uniqueFieldId,
								name: fieldName,
								fieldId,
								type: fieldType,
								cascadingOption: newOption,
							},
						},
					},
				} as CascadingSelectIsEditableMutationResponse,
			});
		},
		[commit, fieldId, fieldName, fieldType, onSubmit, uniqueFieldId],
	);

	const actions = useFieldInlineEditActions({
		attributes,
		fieldId,
		fieldName,
		fieldType,
		initialValue,
		onSubmit: handleSubmit,
		onSubmitFailed,
		onSubmitSucceeded,
		onUpdateValue: setUpdatedValue,
		updatedValue,
		isValueEqual: isEqualSelection,
		clipboardComponentType: 'cascadingSelect',
	});

	const {
		hasServerValidationError,
		handleCancel,
		handleEdit,
		handleCopy,
		handleCut,
		handlePaste,
		handleConfirm,
		handleChange,
		invalidMessage,
		isEditing,
	} = actions;
	// #endregion Action

	// #region View
	const renderReadView = useCallback(
		() => (
			<MaybeUFOSegment>
				<CascadingSelectReadView
					fragmentRef={data}
					isTruncated={isTruncated}
					shouldRenderHoverPopover={shouldRenderHoverPopover}
				/>
			</MaybeUFOSegment>
		),
		[data, isTruncated, shouldRenderHoverPopover],
	);

	const getEditViewProps = (fieldProps: ValidationFieldProps): CascadingSelectEditViewProps => ({
		...fieldProps,
		wrapper: EditViewWrapper,
		fieldId: uniqueFieldId,
		autoFocus: true,
		parentOptionValue: updatedValue?.parentOptionValue,
		childOptionValue: updatedValue?.childOptionValue,
		menuPosition,
		onChange: handleChange,
		spacing,
		...(isExperimentEnabled && {
			value: !updatedValue || !updatedValue.parentOptionValue?.id ? null : updatedValue,
		}),
	});

	return (
		<FieldInlineEditLiteWithEntryPoint
			editViewPopup={editViewPopup}
			editViewPopupAlignBlock={editViewPopupAlignBlock}
			editViewPopupTarget={editViewPopupTarget}
			editViewPopupWidth={editViewPopupWidth}
			editViewPopupMinWidth="small"
			editViewEntryPoint={CascadingEditViewEntryPoint}
			editViewEntryPointParams={{}}
			getEditViewProps={getEditViewProps}
			fieldName={fieldName}
			hasUnsubmittedChanges={hasServerValidationError}
			invalidMessage={invalidMessage}
			isEditing={isEditing}
			isEditable={isFieldEditable}
			onCancel={handleCancel}
			onConfirm={handleConfirm}
			onEdit={handleEdit}
			onCopy={handleCopy}
			onCut={handleCut}
			onPaste={handlePaste}
			readViewFitContainerHeight={readViewFitContainerHeight}
			readView={renderReadView}
			hideActionButtons={hideActionButtons}
			hasReadViewTruncateDisabled={shouldRenderHoverPopover}
		/>
	);
	// #endregion Action
};

export const CascadingSelectInlineEditView = ({
	fragmentRef,
	...props
}: CascadingSelectInlineEditViewProps): JSX.Element => {
	const data = useFragment<CascadingSelectFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment cascadingSelect_issueFieldCascadingSelectInlineEditFull_CascadingSelectInlineEditView_fragmentRef on JiraCascadingSelectField {
				...cascadingSelect_issueFieldCascadingSelectInlineEditFull_CascadingSelectInlineEditViewIsEditable_fragmentRef
				fieldConfig {
					isEditable
				}
			}
		`,
		fragmentRef,
	);
	return (
		<CascadingSelectInlineEditViewIsEditable
			{...props}
			fragmentRef={data}
			isEditable={data.fieldConfig?.isEditable ?? false}
		/>
	);
};

const wrapperStyles = xcss({
	zIndex: 'modal',
	position: 'relative',
});
